import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { getIteratorRunCount, getIteratorValues } from '@/utils/iterator.utils';
import { useModelPricesMaps } from '@/state/workflow.state';
import { FF_NORMALIZE_IMPORTED_MODEL_NAMES } from '@/consts/featureFlags';
import { getModelPriceInfo, resolveModelRunCost, RELEVANT_PRICING_MODEL_PARAMS } from '@/components/Nodes/Utils';
import { isUserImportedModelNode } from '@/utils/model-import.utils';
import { isVideoOutputNode } from '@/utils/model-video.utils';
import { useFlowView, useNodesParams } from '@/components/Recipe/FlowContext';
import { useActivePromotions } from './useActivePromotions';
import { useDeepEqualMemo } from './useDeepEqualMemo';
import { useWithLoader } from './useWithLoader';
import type { PrecedingIteratorData } from './usePrecedingIterators';
import type { IteratorRunMode, Node } from '@/types/node';
import type { Model, ModelBaseNodeData } from '@/types/nodes/model';
import type { ResolvedModelPrice } from '@/components/Nodes/Utils';
import type { RunCostResponse } from '@/api/api.shared.types';

const DEBOUNCE_WAIT = 300;

export type ModelCostQuote = RunCostResponse & { isLoading: boolean };

const getQuotedPrice = ({ cost }: ModelCostQuote): ResolvedModelPrice | undefined =>
  cost === undefined ? undefined : { credits: cost, isByok: false };

type CalculationRelevantData = {
  model: Model;
  params: Record<(typeof RELEVANT_PRICING_MODEL_PARAMS)[number], unknown>;
  isImportedModel: boolean;
  isVideoOutput: boolean;
};

export const useGetCost = ({
  enabled = true,
  precedingIterators,
  calculateCost,
  runMode = 'parallel',
  selectedNodes,
  runs = 1,
  modelCostQuotes,
}: {
  enabled?: boolean;
  precedingIterators: PrecedingIteratorData[] | null;
  calculateCost: () => Promise<{ cost?: number; isDynamicCost?: boolean }>;
  runMode?: IteratorRunMode;
  selectedNodes?: Node[] | Node;
  runs?: number;
  modelCostQuotes?: ReadonlyMap<string, ModelCostQuote>;
}) => {
  const [cost, setCost] = useState<number>();
  const [recalculate, setRecalculate] = useState(0);
  const [isDynamicCost, setIsDynamicCost] = useState(false);
  const timeoutRef = useRef<number>(undefined);
  const modelPricesMaps = useModelPricesMaps();
  const flowView = useFlowView();
  const normalizeImportedModelNames = useFeatureFlagEnabled(FF_NORMALIZE_IMPORTED_MODEL_NAMES);
  const promotions = useActivePromotions();

  const modelNodes = useMemo(() => {
    const nodesArray = Array.isArray(selectedNodes) ? selectedNodes : selectedNodes ? [selectedNodes] : [];
    return nodesArray.filter(
      (node): node is Node & { data: ModelBaseNodeData & { model: Model } } =>
        !!(node.data as ModelBaseNodeData | undefined)?.model,
    );
  }, [selectedNodes]);

  const modelNodeIds = useMemo(() => modelNodes.map((n) => n.id), [modelNodes]);
  const resolvedParamsByNode = useNodesParams(modelNodeIds, RELEVANT_PRICING_MODEL_PARAMS);

  // Select/drag replace the node object even when model + resolved pricing params are unchanged.
  // Deep-equal restores the old isEqual gate: same model + params must not bump `recalculate`.
  const calculationRelevantData = useDeepEqualMemo<CalculationRelevantData[]>(
    modelNodes.map((node, i) => ({
      model: node.data.model,
      params: resolvedParamsByNode[i] as CalculationRelevantData['params'],
      isImportedModel: isUserImportedModelNode(node.data),
      isVideoOutput: isVideoOutputNode(node.id, flowView),
    })),
  );

  // This is used to decide when we need to recalculate the cost
  const totalValues = useMemo(() => {
    if (!precedingIterators?.length) return 0;
    const iteratorValueCounts = precedingIterators.map(
      (iterator) =>
        getIteratorValues(iterator.iteratorNode.id, iterator.iteratorNode.data, (id) =>
          flowView.listSelectorOptions(id),
        ).length,
    );

    return getIteratorRunCount(iteratorValueCounts, runMode);
  }, [precedingIterators, flowView, runMode]);

  const totalIteratorValues = useMemo(() => {
    if (!precedingIterators?.length) return 0;
    return precedingIterators.reduce(
      (total, iterator) =>
        total +
        getIteratorValues(iterator.iteratorNode.id, iterator.iteratorNode.data, (id) =>
          flowView.listSelectorOptions(id),
        ).length,
      0,
    );
  }, [precedingIterators, flowView]);

  const resolvedPriceInfos = useMemo(
    () =>
      calculationRelevantData.map((data) =>
        resolveModelRunCost(
          data.model,
          modelPricesMaps,
          data.params,
          normalizeImportedModelNames,
          promotions,
          data.isImportedModel,
          data.isVideoOutput,
        ),
      ),
    [calculationRelevantData, modelPricesMaps, normalizeImportedModelNames, promotions],
  );

  // Primitive so price-map / promo / runs changes refresh `cost` without putting `fetchCost` in
  // the fetch effect (select/drag recreates that function).
  const unitCredits = useMemo(
    () => resolvedPriceInfos.reduce((sum, info) => sum + (info?.credits || 0), 0),
    [resolvedPriceInfos],
  );
  const costInputs = useDeepEqualMemo({
    calculationRelevantData,
    modelNodeIds,
    totalValues,
    totalIteratorValues,
    runs,
    runMode,
    unitCredits,
  });
  const currentCostInputs = useRef(costInputs);
  currentCostInputs.current = costInputs;
  const [batchQuote, setBatchQuote] = useState<{ inputs: typeof costInputs; data: RunCostResponse }>();

  useEffect(() => {
    setRecalculate((prev) => prev + 1);
  }, [costInputs]);

  // Raw per-model price info, before duration is baked into per-second models. The split below uses these so the
  // per-second portion stays expressed as a rate ("3 / sec") instead of "rate × duration" credits.
  const rawPriceInfos = useMemo(
    () =>
      calculationRelevantData.map((data, index) => {
        const quote = modelCostQuotes?.get(modelNodes[index].id);
        if (quote) return getQuotedPrice(quote);
        return getModelPriceInfo(data.model, modelPricesMaps, data.params, {
          normalizeImportedModelNames,
          promotions,
          isImportedModel: data.isImportedModel,
          isVideoOutput: data.isVideoOutput,
        });
      }),
    [calculationRelevantData, modelPricesMaps, normalizeImportedModelNames, promotions, modelCostQuotes, modelNodes],
  );

  const costQuotes = modelNodes.map((node) => modelCostQuotes?.get(node.id));
  const hasCostQuotes = costQuotes.some(Boolean);
  const quotedUnitCredits = modelCostQuotes?.size
    ? resolvedPriceInfos.reduce((sum, info, index) => sum + (costQuotes[index]?.cost ?? info?.credits ?? 0), 0)
    : unitCredits;
  const getPredictableCost = useCallback(() => quotedUnitCredits * runs, [quotedUnitCredits, runs]);

  // `isByok` / `isPerSecond` track the shape of the resolved `cost` field — `isPerSecond=true` means `cost`
  // is still a per-second rate (e.g. unknown-duration fallback) rather than a duration-baked total.
  // Consumers like ModelNodeFooter combine `cost` + `isPerSecond` to pick "X credits" vs "X / sec".
  const isByok = useMemo(() => resolvedPriceInfos.some((info) => info?.isByok === true), [resolvedPriceInfos]);
  const isPerSecond = useMemo(
    () => resolvedPriceInfos.some((info) => info?.isPerSecond === true),
    [resolvedPriceInfos],
  );

  // The split below is computed from the raw per-model info so the per-second portion stays a rate
  // ("3 / sec / run") regardless of whether duration is known.
  // With iterators, each model runs once per iteration value, so per-inference scales by `totalValues × runs`.
  // `totalValues` is 0 when there are no iterators — clamp to 1. Per-second stays a per-run rate, so it doesn't
  // scale with either runs or iterator count.
  const totalRunMultiplier = Math.max(totalValues, 1) * runs;

  const perInferenceCost = useMemo(() => {
    const rate = rawPriceInfos.reduce((sum, info) => sum + (info?.isPerSecond ? 0 : info?.credits || 0), 0);
    return rate * totalRunMultiplier;
  }, [rawPriceInfos, totalRunMultiplier]);

  // Undiscounted per-inference total for the struck-through price; undefined when nothing in the selection is promoted.
  const fullPerInferenceCost = useMemo(() => {
    if (!rawPriceInfos.some((info) => info?.fullCredits !== undefined)) {
      return undefined;
    }
    const rate = rawPriceInfos.reduce(
      (sum, info) => sum + (info?.isPerSecond ? 0 : (info?.fullCredits ?? info?.credits ?? 0)),
      0,
    );
    return rate * totalRunMultiplier;
  }, [rawPriceInfos, totalRunMultiplier]);

  const promo = useMemo(() => rawPriceInfos.find((info) => info?.promo !== undefined)?.promo, [rawPriceInfos]);

  const perSecondRate = useMemo(
    () => rawPriceInfos.reduce((sum, info) => sum + (info?.isPerSecond ? info?.credits || 0 : 0), 0),
    [rawPriceInfos],
  );

  const getCost = useCallback(async () => {
    if (precedingIterators?.length) {
      let data: RunCostResponse;
      try {
        data = await calculateCost();
      } catch (error) {
        if (!hasCostQuotes) throw error;
        data = { isDynamicCost: true };
      }
      if (currentCostInputs.current === costInputs) {
        setBatchQuote({ inputs: costInputs, data });
        setIsDynamicCost(!!data.isDynamicCost);
        setCost(data.cost);
      }
      return data.cost ?? 0;
    }
    const cost = getPredictableCost();
    setCost(cost);
    return cost;
  }, [calculateCost, getPredictableCost, precedingIterators?.length, costInputs, hasCostQuotes]);

  const { execute: fetchCost, isLoading } = useWithLoader(getCost);

  const hasIterators = !!precedingIterators?.length;
  const hasUnresolvedQuote = costQuotes.some((quote) => quote && (quote.cost === undefined || quote.isDynamicCost));
  const usesBatchQuote = hasIterators && hasCostQuotes;
  const batchData = batchQuote?.inputs === costInputs ? batchQuote.data : undefined;
  const showBatchTotal = hasIterators && hasUnresolvedQuote;
  const quotedCost = hasCostQuotes && !hasIterators ? getPredictableCost() : cost;

  useEffect(() => {
    if (!enabled) return;

    // Clear any pending debounced fetch
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    // Synchronous predictable-cost path doesn't need debouncing — run immediately
    // so the UI doesn't briefly render `cost = undefined` (which other consumers
    // coerce to "0 credits").
    if (!hasIterators) {
      void fetchCost();
      return;
    }

    // Debounce only the async iterator path to avoid spamming calculateCost.
    timeoutRef.current = window.setTimeout(() => {
      void fetchCost();
    }, DEBOUNCE_WAIT);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // `fetchCost` is intentionally omitted: select/drag recreates it even when price inputs
    // did not change. Listing it here POSTs `/cost` on every drag.
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [recalculate, enabled, hasIterators]);

  return {
    cost: usesBatchQuote ? batchData?.cost : hasUnresolvedQuote ? undefined : quotedCost,
    isLoading: usesBatchQuote ? enabled && !batchData : isLoading || costQuotes.some((quote) => quote?.isLoading),
    forceGetCost: getCost,
    isDynamicCost: usesBatchQuote
      ? !!batchData?.isDynamicCost
      : isDynamicCost || costQuotes.some((quote) => quote?.isDynamicCost),
    isByok,
    isPerSecond: showBatchTotal ? false : isPerSecond,
    perInferenceCost: showBatchTotal ? batchData?.cost : hasUnresolvedQuote ? undefined : perInferenceCost,
    fullPerInferenceCost: hasUnresolvedQuote ? undefined : fullPerInferenceCost,
    promo: showBatchTotal ? undefined : promo,
    perSecondRate: showBatchTotal ? 0 : perSecondRate,
  };
};
